class BcsProductCardPurchase extends HTMLElement {
  #abortController = new AbortController();
  #oneTimePrice = '';

  connectedCallback() {
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();

    const priceData = this.querySelector('[data-card-prices]')?.textContent;
    if (priceData) {
      try {
        this.#oneTimePrice = JSON.parse(priceData).oneTime || '';
      } catch (error) {
        console.warn('[bcs-product-card-purchase] Could not read card prices.', error);
      }
    }

    this.addEventListener('change', this.#handleChange, { signal: this.#abortController.signal });
    this.addEventListener('click', this.#handleClick, { signal: this.#abortController.signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal: this.#abortController.signal });
    document.addEventListener('click', this.#handleDocumentClick, { signal: this.#abortController.signal });
    this.#syncState();
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #handleChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    this.#syncState();
  };

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-card-plan-trigger]');
    if (trigger) {
      event.preventDefault();
      this.#setDropdownOpen(trigger.getAttribute('aria-expanded') !== 'true');
      return;
    }

    const planOption = event.target.closest('[data-card-plan-option]');
    if (!(planOption instanceof HTMLButtonElement)) return;

    event.preventDefault();
    for (const option of this.querySelectorAll('[data-card-plan-option]')) {
      option.setAttribute('aria-selected', String(option === planOption));
    }

    const subscriptionRadio = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[value="subscription"]')
    );
    if (subscriptionRadio) subscriptionRadio.checked = true;

    this.#syncState();
    this.#setDropdownOpen(false);
    this.querySelector('[data-card-plan-trigger]')?.focus();
  };

  #handleKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#isDropdownOpen()) return;
    event.preventDefault();
    this.#setDropdownOpen(false);
    this.querySelector('[data-card-plan-trigger]')?.focus();
  };

  #handleDocumentClick = (event) => {
    if (event.target instanceof Node && !this.contains(event.target)) this.#setDropdownOpen(false);
  };

  #syncState() {
    const subscriptionRadio = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[value="subscription"]')
    );
    const planInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-card-selling-plan-input]'));
    const selectedPlan = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-card-plan-option][aria-selected="true"]')
    );
    const isSubscription = Boolean(subscriptionRadio?.checked);

    for (const option of this.querySelectorAll('[data-card-purchase-option]')) {
      const radio = option.querySelector('input[type="radio"]');
      option.classList.toggle(
        'featured-products__purchase-option--selected',
        radio instanceof HTMLInputElement && radio.checked
      );
    }

    if (planInput) {
      planInput.disabled = !isSubscription;
      planInput.value = selectedPlan?.dataset.value || '';
    }

    const planLabel = this.querySelector('[data-card-plan-label]');
    if (planLabel) planLabel.textContent = selectedPlan?.dataset.label || '';

    const subscriptionLabel = this.querySelector('[data-card-subscription-label]');
    const discount = Number(selectedPlan?.dataset.discount || 0);
    if (subscriptionLabel) {
      subscriptionLabel.textContent = discount > 0 ? `Autoship (${discount}% off)` : 'Autoship';
    }

    const addText = this.querySelector('[data-card-add-text]');
    if (addText) {
      const price = isSubscription ? selectedPlan?.dataset.price || this.#oneTimePrice : this.#oneTimePrice;
      addText.textContent = `Add • ${price}`;
    }

    if (!isSubscription) this.#setDropdownOpen(false);
  }

  #isDropdownOpen() {
    return this.querySelector('[data-card-plan-trigger]')?.getAttribute('aria-expanded') === 'true';
  }

  #setDropdownOpen(open) {
    const trigger = this.querySelector('[data-card-plan-trigger]');
    const menu = /** @type {HTMLElement | null} */ (this.querySelector('[data-card-plan-menu]'));
    if (!trigger || !menu) return;

    trigger.setAttribute('aria-expanded', String(open));
    menu.hidden = !open;
    this.toggleAttribute('data-dropdown-open', open);
  }
}

if (!customElements.get('bcs-product-card-purchase')) {
  customElements.define('bcs-product-card-purchase', BcsProductCardPurchase);
}
